import React, { type ReactNode, useCallback, useState } from 'react';

import { useIntl } from 'react-intl-next';

import { UI_EVENT_TYPE } from '@atlaskit/analytics-gas-types';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button/new';
import { cssMap, cx } from '@atlaskit/css';
import mergeRefs from '@atlaskit/ds-lib/merge-refs';
import { ButtonMenuItem as MenuButtonItemPrimitive } from '@atlaskit/navigation-system/side-nav-items/button-menu-item';
import {
	FlyoutMenuItem,
	FlyoutMenuItemContent,
	FlyoutMenuItemTrigger,
} from '@atlaskit/navigation-system/side-nav-items/flyout-menu-item';
import { MenuList } from '@atlaskit/navigation-system/side-nav-items/menu-list';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { ANALYTICS_CHANNEL } from '../../constants';
import type {
	GlobalShortcutActionsItem,
	GlobalShortcutConfiguration,
	GlobalShortcutExpandItem,
	GlobalShortcutLinkItem,
} from '../../types';
import { getUrlMaybe } from '../../utils/get-url-maybe';
import { MenuContent } from '../menu-content';
import { MenuLinkItem } from '../menu-link-item';
import { MenuLoadingItem } from '../menu-loading-item';
import { MenuSection } from '../menu-section';

import { messages } from './messages';

const styles = cssMap({
	root: {
		marginBottom: token('space.050'),
	},
});

export type MenuExpandItemProps = Omit<GlobalShortcutExpandItem, 'ref' | 'type'> & {
	actions?: GlobalShortcutActionsItem[];
	children?: ReactNode;
	disabled?: boolean;
	loading?: boolean;
	name?: string;
	onDisable?: () => void;
	optionalRef?: GlobalShortcutConfiguration['ref'];
	product?: string;
	promotedItem?: GlobalShortcutExpandItem | GlobalShortcutLinkItem;
};

export const MenuExpandItem = ({
	icon,
	label,
	labelElem,
	analyticsName,
	items,
	disabled,
	onDisable,
	promotedItem,
	loading,
	onClick,
	optionalRef,
	product,
}: MenuExpandItemProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const mergedRefs = mergeRefs(optionalRef ? [optionalRef] : []);

	const [isOpen, setIsOpen] = useState(false);

	const closeFlyout = useCallback(() => setIsOpen(false), []);

	const toggleFlyout = useCallback(() => {
		createAnalyticsEvent({
			eventType: UI_EVENT_TYPE,
			action: 'clicked',
			actionSubject: 'globalNavigation',
			actionSubjectId: 'navigationItem',
			attributes: {
				name: analyticsName,
				isExpanding: !isOpen,
			},
		}).fire(ANALYTICS_CHANNEL);
		setIsOpen((prev) => !prev);
		onClick?.();
	}, [createAnalyticsEvent, analyticsName, isOpen, onClick]);

	const createLinkItem = useCallback(
		(shortcut: GlobalShortcutExpandItem | GlobalShortcutLinkItem, index: number) => {
			if (shortcut.type === 'expand') {
				return (
					<MenuExpandItem
						key={`shortcut-${index}`}
						icon={shortcut.icon}
						label={shortcut.label}
						labelElem={shortcut.labelElem}
						items={shortcut.items}
						disabled={disabled}
						analyticsName={analyticsName}
						onClick={shortcut.onClick}
						onRightClick={shortcut.onRightClick}
						product={product}
					/>
				);
			}
			return (
				<MenuLinkItem
					key={shortcut?.apsKey ?? `shortcut-${index}`}
					apsKey={shortcut.apsKey}
					icon={shortcut.icon}
					url={getUrlMaybe(shortcut)}
					label={shortcut.label}
					labelElem={shortcut.labelElem}
					disabled={disabled}
					ariaLabel={formatMessage(messages.siteAriaLabel, {
						name: shortcut.label,
					})}
					analyticsName={analyticsName}
					onClick={shortcut.onClick}
					onRightClick={shortcut.onRightClick}
					product={product}
				/>
			);
		},
		[analyticsName, disabled, formatMessage, product],
	);

	// If loading state then show loading placeholder
	if (loading === true) {
		return <MenuLoadingItem icon={icon} label={labelElem ?? label} />;
	}

	if (disabled) {
		onDisable?.();

		return (
			<MenuButtonItemPrimitive elemBefore={icon} isDisabled>
				{labelElem ?? label}
			</MenuButtonItemPrimitive>
		);
	}

	return (
		<FlyoutMenuItem isOpen={isOpen} onOpenChange={setIsOpen}>
			<FlyoutMenuItemTrigger ref={mergedRefs} elemBefore={icon} onClick={toggleFlyout}>
				{labelElem ?? label}
			</FlyoutMenuItemTrigger>
			<FlyoutMenuItemContent onClose={closeFlyout}>
				<MenuContent heading={labelElem ?? label}>
					{promotedItem && (
						<MenuSection>
							<MenuList key="gas-promoted-sites">{createLinkItem(promotedItem, 1)}</MenuList>
						</MenuSection>
					)}
					<MenuSection label={formatMessage(messages.allSites)}>
						<MenuList key="gas-all-sites">{items?.map(createLinkItem)}</MenuList>
					</MenuSection>
					<Box xcss={cx(styles.root)}>
						<Button shouldFitContainer appearance="default" onClick={closeFlyout}>
							{formatMessage(messages.closeButtonAriaLabel)}
						</Button>
					</Box>
				</MenuContent>
			</FlyoutMenuItemContent>
		</FlyoutMenuItem>
	);
};
